import type { TOptions } from 'i18next';
import type {
  PolicyViolationLabel,
  RuleEvaluationNode,
} from '@http/queries/useFetchGovernanceIssues';
import {
  getRuleFieldLabel,
  getRuleOperatorLabel,
} from '../CustomPolicy/PolicyRuleLabels';

type Translate = (key: string, options?: TOptions) => string;

type RelevantCondition = {
  condition: RuleEvaluationNode;
  negated: boolean;
};

function collectRelevantConditions(
  condition: RuleEvaluationNode | undefined,
  expectedPass: boolean,
  negated: boolean,
  conditions: RelevantCondition[],
) {
  if (!condition || condition.pass !== expectedPass) {
    return;
  }

  if (!condition.matchMode) {
    conditions.push({ condition, negated });
    return;
  }

  const childExpectedPass = condition.negate ? !expectedPass : expectedPass;
  const childNegated = condition.negate ? !negated : negated;

  for (const child of [
    ...(condition.rules ?? []),
    ...(condition.groups ?? []),
  ]) {
    collectRelevantConditions(
      child,
      childExpectedPass,
      childNegated,
      conditions,
    );
  }
}

function formatCondition(
  { condition, negated }: RelevantCondition,
  t: Translate,
) {
  if (!condition.field || !condition.operator) {
    return t('policyConditionUnavailable');
  }

  const value = condition.matchedTerms?.length
    ? condition.matchedTerms.join(', ')
    : condition.value;

  if (!value) {
    return t('policyConditionUnavailable');
  }

  return t(negated ? 'policyConditionNotMet' : 'policyConditionMatched', {
    field: t(getRuleFieldLabel(condition.field)),
    operator: t(getRuleOperatorLabel(condition.operator)),
    value,
  });
}

export function formatIssueDetail(
  rule: RuleEvaluationNode,
  violationLabel: PolicyViolationLabel | undefined,
  t: Translate,
) {
  if (!violationLabel) {
    return t('policyConditionUnavailable');
  }

  const negated = violationLabel === 'required';
  const relevantConditions: RelevantCondition[] = [];
  collectRelevantConditions(rule, !negated, negated, relevantConditions);

  if (relevantConditions.length === 0) {
    return t('policyConditionUnavailable');
  }

  return [
    ...new Set(
      relevantConditions.map((condition) => formatCondition(condition, t)),
    ),
  ].join('; ');
}
